import { Button } from "@mui/material";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import { UserGroupDetailsContextProvider } from "./UserGroupDetailsContext";
import UserGroupDetailsPageContainer from "./UserGroupDetailsPageContainer";
import { getCreateClientUserGroupRequest, getInitialUserGroupDetailsState } from "./userGroupDetailsState";

const createClientUserGroup = withErrorHandling(adminApi.createClientUserGroup);

const NewUserGroupPage = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const navigate = useNavigate();
  const { hasAnyPermission } = useClientContext();

  const [state, setState] = useState(getInitialUserGroupDetailsState);
  const [saving, setSaving] = useState(false);

  const [availableUsers, fetchUsersError] = useFetch(adminApi.getAvailableClientUsers);

  const hasPermissionToManage = hasAnyPermission(["ManageOrganizationUsers"]);
  if (!hasPermissionToManage) {
    return <Navigate to=".." />;
  }

  if (fetchUsersError) {
    logError(fetchUsersError, "[NewUserGroupPage] getAvailableClientUsers");
    return <DataLoadingFailed />;
  }

  if (availableUsers === undefined) {
    return <InlineLoader />;
  }

  const handleSave = async () => {
    setSaving(true);
    const [, error] = await createClientUserGroup(getCreateClientUserGroupRequest(state));
    setSaving(false);

    if (error) {
      logError(error, "[NewUserGroupPage] createClientUserGroup");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("User group created successfully");

    setTimeout(() => navigate(".."), 300);
  };

  return (
    <>
      <GeneralPageHeader title="New User Group" showDefaultBackButtonTo="..">
        <Button variant="contained" disabled={!state.validationResult.isValid} onClick={handleSave} loading={saving}>
          Save
        </Button>
      </GeneralPageHeader>

      <UserGroupDetailsContextProvider
        state={state}
        setState={setState}
        hasPermissionToManage={hasPermissionToManage}
        availableMembers={availableUsers}
      >
        <UserGroupDetailsPageContainer />
      </UserGroupDetailsContextProvider>
    </>
  );
};

export default NewUserGroupPage;
